import { getClientBuildInfo, type ClientBuildInfo } from './client-build'

export const INTERACTION_HEADER_NAMES = {
  interactionId: "x-hp-interaction-id",
  originKind: "x-hp-origin-kind",
  originRoute: "x-hp-origin-route",
  originUiSurface: "x-hp-origin-ui-surface",
  originUiAction: "x-hp-origin-ui-action",
  originUiElement: "x-hp-origin-ui-element",
  clientVersion: 'x-hp-client-version',
  clientBuildId: 'x-hp-client-build-id',
} as const;

export interface InteractionOrigin {
  kind?: string;
  route?: string;
  uiSurface?: string;
  uiAction?: string;
  uiElement?: string;
}

export interface InteractionContext {
  id: string;
  origin: InteractionOrigin;
}

function trimTo(value: string | undefined, maxLength: number): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim().slice(0, maxLength);
  return trimmed.length > 0 ? trimmed : undefined;
}

export function createInteractionId(prefix = "ui"): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}_${crypto.randomUUID().replace(/-/g, "")}`;
  }

  return `${prefix}_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export function normalizeInteractionOrigin(origin?: InteractionOrigin): InteractionOrigin {
  return {
    kind: trimTo(origin?.kind, 24),
    route: trimTo(origin?.route, 200),
    uiSurface: trimTo(origin?.uiSurface, 80),
    uiAction: trimTo(origin?.uiAction, 80),
    uiElement: trimTo(origin?.uiElement, 80),
  };
}

export function normalizeInteractionContext(
  input?: Partial<InteractionContext> & { id?: string; interactionId?: string; origin?: InteractionOrigin }
): InteractionContext {
  const id = trimTo(input?.id ?? input?.interactionId, 120) ?? createInteractionId();

  return {
    id,
    origin: normalizeInteractionOrigin(input?.origin),
  };
}

export function getInteractionHeaders(context?: Partial<InteractionContext>): Record<string, string> {
  const normalized = normalizeInteractionContext(context);
  const clientBuild = getClientBuildInfo()
  const headers: Record<string, string> = {
    [INTERACTION_HEADER_NAMES.interactionId]: normalized.id,
  };

  if (normalized.origin.kind) {
    headers[INTERACTION_HEADER_NAMES.originKind] = normalized.origin.kind;
  }
  if (normalized.origin.route) {
    headers[INTERACTION_HEADER_NAMES.originRoute] = normalized.origin.route;
  }
  if (normalized.origin.uiSurface) {
    headers[INTERACTION_HEADER_NAMES.originUiSurface] = normalized.origin.uiSurface;
  }
  if (normalized.origin.uiAction) {
    headers[INTERACTION_HEADER_NAMES.originUiAction] = normalized.origin.uiAction;
  }
  if (normalized.origin.uiElement) {
    headers[INTERACTION_HEADER_NAMES.originUiElement] = normalized.origin.uiElement;
  }
  if (clientBuild.version) {
    headers[INTERACTION_HEADER_NAMES.clientVersion] = clientBuild.version
  }
  if (clientBuild.buildId) {
    headers[INTERACTION_HEADER_NAMES.clientBuildId] = clientBuild.buildId
  }

  return headers;
}

export function readClientBuildHeaders(headers: Headers): ClientBuildInfo | undefined {
  const version = trimTo(headers.get(INTERACTION_HEADER_NAMES.clientVersion) ?? undefined, 120)
  const buildId = trimTo(headers.get(INTERACTION_HEADER_NAMES.clientBuildId) ?? undefined, 120)

  if (!version && !buildId) {
    return undefined
  }

  return { version, buildId }
}

export function readInteractionHeaders(headers: Headers): InteractionContext | undefined {
  const id = trimTo(headers.get(INTERACTION_HEADER_NAMES.interactionId) ?? undefined, 120);
  if (!id) {
    return undefined;
  }

  return {
    id,
    origin: normalizeInteractionOrigin({
      kind: headers.get(INTERACTION_HEADER_NAMES.originKind) ?? undefined,
      route: headers.get(INTERACTION_HEADER_NAMES.originRoute) ?? undefined,
      uiSurface: headers.get(INTERACTION_HEADER_NAMES.originUiSurface) ?? undefined,
      uiAction: headers.get(INTERACTION_HEADER_NAMES.originUiAction) ?? undefined,
      uiElement: headers.get(INTERACTION_HEADER_NAMES.originUiElement) ?? undefined,
    }),
  };
}

let currentInteraction: InteractionContext | undefined;

export function getCurrentInteraction(): InteractionContext | undefined {
  return currentInteraction;
}

export function setCurrentInteraction(context?: Partial<InteractionContext>): InteractionContext | undefined {
  if (!context) {
    currentInteraction = undefined;
    return currentInteraction;
  }

  currentInteraction = normalizeInteractionContext(context);
  return currentInteraction;
}

export function beginInteraction(origin?: InteractionOrigin): InteractionContext {
  return setCurrentInteraction({
    id: createInteractionId(),
    origin,
  })!;
}

function inferRoute(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.location.pathname;
}

export function ensureInteraction(origin?: InteractionOrigin): InteractionContext {
  const existing = getCurrentInteraction();
  if (existing) {
    if (
      origin?.kind ||
      origin?.route ||
      origin?.uiSurface ||
      origin?.uiAction ||
      origin?.uiElement
    ) {
      return setCurrentInteraction({
        id: existing.id,
        origin: {
          kind: origin.kind ?? existing.origin.kind,
          route: origin.route ?? existing.origin.route,
          uiSurface: origin.uiSurface ?? existing.origin.uiSurface,
          uiAction: origin.uiAction ?? existing.origin.uiAction,
          uiElement: origin.uiElement ?? existing.origin.uiElement,
        },
      })!;
    }

    return existing;
  }

  return beginInteraction({
    kind: origin?.kind ?? "ui",
    route: origin?.route ?? inferRoute(),
    uiSurface: origin?.uiSurface,
    uiAction: origin?.uiAction,
    uiElement: origin?.uiElement,
  });
}

export type TelemetryFetchOptions = RequestInit & {
  interaction?: InteractionOrigin | InteractionContext;
};

export interface ConvexInteractionArgs {
  _interactionId?: string;
  _originKind?: string;
  _originRoute?: string;
  _originUiSurface?: string;
  _originUiAction?: string;
  _originUiElement?: string;
  _clientVersion?: string;
  _clientBuildId?: string;
}

function isSameOriginApiRequest(input: RequestInfo | URL): boolean {
  const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

  if (url.startsWith("/api/")) {
    return true;
  }

  if (typeof window === "undefined") {
    return false;
  }

  try {
    const resolved = new URL(url, window.location.origin);
    return resolved.origin === window.location.origin && resolved.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

export async function fetchWithTelemetry(
  input: RequestInfo | URL,
  options: TelemetryFetchOptions = {}
): Promise<Response> {
  if (!isSameOriginApiRequest(input)) {
    return fetch(input, options);
  }

  const { interaction, headers: initHeaders, ...rest } = options;
  const headers = new Headers(initHeaders);
  const context = ensureInteraction(
    "origin" in (interaction ?? {}) || "id" in (interaction ?? {})
      ? (interaction as InteractionContext).origin
      : (interaction as InteractionOrigin | undefined)
  );

  for (const [key, value] of Object.entries(getInteractionHeaders(context))) {
    headers.set(key, value);
  }

  return fetch(input, {
    ...rest,
    headers,
  });
}

export function getConvexInteractionArgs(origin?: InteractionOrigin | InteractionContext): ConvexInteractionArgs {
  const context = ensureInteraction(
    origin && 'origin' in origin
      ? origin.origin
      : origin
  );
  const clientBuild = getClientBuildInfo()

  return {
    _interactionId: context.id,
    _originKind: context.origin.kind,
    _originRoute: context.origin.route,
    _originUiSurface: context.origin.uiSurface,
    _originUiAction: context.origin.uiAction,
    _originUiElement: context.origin.uiElement,
    _clientVersion: clientBuild.version,
    _clientBuildId: clientBuild.buildId,
  };
}
